import { Accordion } from '@Components/common/accordion'
import {
  AlertDialog,
  Badge,
  Button,
  Card,
  Container,
  DropdownMenu,
  Flex,
  Heading,
  IconButton,
  Separator,
  TextField,
} from '@radix-ui/themes'
import { Accordion as RadixAccordion } from 'radix-ui'
import './style.scss'
import { useCallback, useEffect, useMemo, useState } from 'react'
import { DEFAULT_STALE_DATA_TIMEOUT } from '@AppState/app-config'
import { SearchServiceAPI } from '@API/data-services/search-service'
import { RootState } from '@AppState/redux-store'
import { useMutation, useQuery } from '@tanstack/react-query'
import { useSelector } from 'react-redux'
import { WithPermissions } from '@Components/common/with-permissions'
import {
  AdminSearchConfiguration,
  AuthScopes,
  SearchCollection,
} from '@Generated/back-office-client/src'
import { UpsertSearchRule } from './upsert-search-rule'
import { Cross2Icon, TrashIcon } from '@radix-ui/react-icons'

export const SearchConfiguration = () => {
  const { activeCustomerId } = useSelector((state: RootState) => state.users)
  const [openOverrides, setOpenOverrides] = useState<string[]>([])
  const [showAddNewRule, setShowAddNewRule] = useState(false)
  const [currentCollection, setCurrentCollection] = useState<SearchCollection | null>()
  const [newNoResultMessage, setNewNoResultMessage] = useState<string>('')

  const { data: searchCollections } = useQuery({
    queryKey: [`${activeCustomerId}-search-collections`],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: async () => {
      return SearchServiceAPI.getCollections()
    },
  })

  const { data: overrides, refetch: refetchSearchOverrides } = useQuery({
    queryKey: [
      `${activeCustomerId}-${currentCollection ? currentCollection.hostname : ''}-search-overrides`,
    ],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: async () => {
      return SearchServiceAPI.getSearchOverrides(currentCollection!.hostname)
    },
    enabled: !!currentCollection,
  })

  const { data: adminSearchConfig, refetch: refetchAdminSearchConfig } = useQuery({
    queryKey: [
      `${activeCustomerId}-${currentCollection ? currentCollection.hostname : ''}-admin-search-config`,
    ],
    staleTime: DEFAULT_STALE_DATA_TIMEOUT,
    queryFn: async () => {
      return SearchServiceAPI.getAdminSearchConfig(currentCollection!.hostname)
    },
    enabled: !!currentCollection,
  })

  const { mutate: deleteSearchOverride } = useMutation({
    mutationFn: (overrideName: string) => {
      return SearchServiceAPI.deleteSearchOverride(overrideName, currentCollection!.hostname)
    },
    onSuccess: () => {
      return refetchSearchOverridesWithDelay()
    },
  })

  const { mutate: syncAdminSearchConfig } = useMutation({
    mutationFn: (adminSearchConfig: AdminSearchConfiguration) => {
      return SearchServiceAPI.saveAdminSearchConfig(currentCollection!.hostname, adminSearchConfig)
    },
    onSuccess: () => {
      return refetchAdminSearchConfig()
    },
  })

  const refetchSearchOverridesWithDelay = useCallback(() => {
    refetchSearchOverrides()
    // sometimes we need to give typesense a while for all their nodes to be updated with the config changes
    setTimeout(() => {
      refetchSearchOverrides()
    }, 3000)
  }, [refetchSearchOverrides])

  useEffect(() => {
    if (searchCollections?.length && !currentCollection) {
      setCurrentCollection(searchCollections[0])
    }
  }, [currentCollection, searchCollections])

  const existingOverrides = useMemo(() => {
    if (!(overrides && currentCollection)) {
      return null
    }

    return overrides.map((override) => {
      const overrideIsOpen = openOverrides.find((o) => o === override.name)
      const child = (
        <RadixAccordion.Content className="AccordionContent" key={override.name}>
          {overrideIsOpen ? (
            <UpsertSearchRule
              existingOverride={override}
              currentCollection={currentCollection}
              onClose={() => setShowAddNewRule(false)}
              onSubmit={() => {
                // Close the accordion for the override we edited
                const newOpenOverrides = openOverrides.filter((o) => o !== override.name)
                setOpenOverrides(newOpenOverrides)
                refetchSearchOverridesWithDelay()
              }}
            />
          ) : null}
        </RadixAccordion.Content>
      )

      const actionButton = (
        <AlertDialog.Root>
          <AlertDialog.Trigger onClick={(e) => e.stopPropagation()}>
            <TrashIcon className="delete-override" />
          </AlertDialog.Trigger>
          <AlertDialog.Content maxWidth="500px" style={{ paddingBottom: '60px' }}>
            <AlertDialog.Cancel>
              <Flex justify="end">
                <IconButton variant="ghost" aria-label="Cancel">
                  <Cross2Icon height="20" width="20" style={{ color: 'black' }} />
                </IconButton>
              </Flex>
            </AlertDialog.Cancel>
            <AlertDialog.Title align="center" style={{ paddingTop: '29px' }}>
              Are you sure?
            </AlertDialog.Title>
            <AlertDialog.Description size="2" style={{ paddingBottom: '18px' }} align="center">
              {override.name} will be permanently deleted and must be recreated if desired.
            </AlertDialog.Description>

            <Flex gap="3" justify="center">
              <AlertDialog.Action
                onClick={() => {
                  deleteSearchOverride(override.name)
                }}>
                <Button color="red">Delete</Button>
              </AlertDialog.Action>
            </Flex>
          </AlertDialog.Content>
        </AlertDialog.Root>
      )

      return { id: override.name, value: override.name, name: override.name, child, actionButton }
    })
  }, [
    currentCollection,
    deleteSearchOverride,
    openOverrides,
    overrides,
    refetchSearchOverridesWithDelay,
  ])

  const collectionOptions = useMemo(() => {
    if (!searchCollections) {
      return null
    }

    return searchCollections.map((collection) => {
      return (
        <DropdownMenu.Item
          key={collection.hostname}
          onClick={() => setCurrentCollection(collection)}>
          {collection.name}
        </DropdownMenu.Item>
      )
    })
  }, [searchCollections])

  const collectionSelector = (
    <DropdownMenu.Root>
      <DropdownMenu.Trigger>
        <Flex align="center" gap="2" style={{ cursor: 'pointer' }}>
          <Button>
            {currentCollection?.name}
            <DropdownMenu.TriggerIcon />
          </Button>
        </Flex>
      </DropdownMenu.Trigger>
      <DropdownMenu.Content>{collectionOptions}</DropdownMenu.Content>
    </DropdownMenu.Root>
  )

  return (
    <Container>
      <Flex className="search-configuration-header">
        <Flex direction="column" gap="3">
          <Heading>Search Configuration</Heading>
        </Flex>
      </Flex>

      {searchCollections ? <Flex>{collectionSelector}</Flex> : null}

      <Card className="no-results-messages">
        <Heading as="h3" className="no-results-header">
          Manage No Result Messages
        </Heading>

        <WithPermissions requiredPermissions={AuthScopes.writesearch}>
          <TextField.Root
            placeholder="No results message..."
            className="no-results-message-input"
            value={newNoResultMessage}
            onChange={(e) => setNewNoResultMessage(e.target.value)}>
            <TextField.Slot></TextField.Slot>
            <Button
              onClick={() => {
                syncAdminSearchConfig({
                  noResultMessages: [
                    ...(adminSearchConfig?.noResultMessages ?? []),
                    newNoResultMessage,
                  ],
                })
                setNewNoResultMessage('')
              }}>
              Add
            </Button>
          </TextField.Root>
        </WithPermissions>

        <Flex gap="2" wrap="wrap">
          {adminSearchConfig?.noResultMessages.map((message) => {
            return (
              <Badge color="gray" key={message}>
                {message}
                <Button
                  color="red"
                  onClick={() => {
                    syncAdminSearchConfig({
                      noResultMessages: adminSearchConfig?.noResultMessages.filter(
                        (m) => m !== message,
                      ),
                    })
                  }}>
                  X
                </Button>
              </Badge>
            )
          })}
        </Flex>
      </Card>

      {showAddNewRule && currentCollection ? (
        <UpsertSearchRule
          currentCollection={currentCollection}
          onClose={() => setShowAddNewRule(false)}
          onSubmit={() => {
            setShowAddNewRule(false)
            refetchSearchOverridesWithDelay()
          }}
        />
      ) : null}

      {showAddNewRule ? null : (
        <WithPermissions requiredPermissions={AuthScopes.writesearch}>
          <Flex direction="column">
            <Button className="add-rule-button" onClick={() => setShowAddNewRule(true)}>
              Add New Pinned Result Rule +
            </Button>
          </Flex>
        </WithPermissions>
      )}

      {overrides ? (
        <Flex className="existing-rule-container" direction="column">
          <Separator size="4" className="existing-rule-container__separator" />
          {existingOverrides ? (
            <Accordion
              items={existingOverrides}
              values={openOverrides}
              onValueChange={setOpenOverrides}
            />
          ) : null}
        </Flex>
      ) : null}
    </Container>
  )
}
